import React from 'react';

import { useCanOpenStudio } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import ContextMenu, { Break, Info, Option } from 'kingpin/atoms/ContextMenu';
import Tooltip from '../../Tooltip';
import PermissionGates from '../../PermissionGates';
import PERMISSIONS from '../../../permissionDefinitions';
import FeatureGate, { FEATURE_GATES } from '../../FeatureGate';
import FleetOpsStaffOnly from '../../Common/FleetOpsStaffOnly';
import EditInStudioOption from '../../EditInStudioOption';
import useReportAnIssueModal from '../../ReportAnIssue/useReportAnIssueModal';
import V3UnpublishDashboard from './V3UnpublishDashboard';

const DashboardMenu = ({
  onStartEditing,
  onCreateNewGadgetClicked,
  onSaveAs,
  createTemplateFrom,
  onDelete,
  onSettingsClicked,
  isFullScreen,
  onFullscreenClicked,
  onExitFullscreenClicked,
  openIFrameForm,
  onImageCardAdded,
  openVideoCardForm,
  openTextCardForm,
  onMetricListAdded,
  onAddScorecardClicked,
  onUsageReportClicked,
  onScheduleClicked,
  openVariableFiltersSlideOut,
  onConfigureVariables,
  onViewSourceTemplate,
  isTemplate,
  isTemplated,
  isDeletable,
  isWallboard,
  isScorecardDashboard,
  isCreatedByMe,
  dashboard,
}: {
  onStartEditing: () => void;
  onCreateNewGadgetClicked: () => void;
  onSaveAs: () => void;
  createTemplateFrom: () => Promise<void>;
  onDelete: () => void;
  onSettingsClicked: () => void;
  isFullScreen: boolean;
  onFullscreenClicked: () => void;
  onExitFullscreenClicked: () => void;
  openIFrameForm: () => void;
  onImageCardAdded: () => void;
  openVideoCardForm: () => void;
  openTextCardForm: () => void;
  onMetricListAdded: () => void;
  onAddScorecardClicked: () => void;
  onUsageReportClicked: () => void;
  onScheduleClicked: () => void;
  openVariableFiltersSlideOut: () => void;
  onConfigureVariables: () => void;
  onViewSourceTemplate: (templateId: string | undefined) => void;
  isTemplate: boolean;
  isTemplated: boolean;
  isDeletable: boolean;
  isWallboard?: boolean;
  isScorecardDashboard: boolean;
  isCreatedByMe: boolean;
  dashboard: DashboardType;
}) => {
  const { open: openReportAnIssue, modal: reportAnIssueModal } =
    useReportAnIssueModal({ type: 'dashboard' });
  const canOpenStudio = useCanOpenStudio();
  return (
    <>
      <ContextMenu testId="dashboard-menu">
        <>
          <PermissionGates.Has
            requiredPermission={
              PERMISSIONS.CONTENT_ACCESS.SET_ANY_AVAILABLE_TO_USERS_OR_PUBLIC
            }
          >
            <Option label={'Settings'} onClick={onSettingsClicked} />
          </PermissionGates.Has>
          {!isTemplated && (
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.DASHBOARDS.EDIT}
            >
              <EditInStudioOption
                contentType="dashboard"
                id={dashboard.id}
                onClassicEdit={onStartEditing}
              />
            </PermissionGates.Has>
          )}
          {!isFullScreen && (
            <Option label={'Fullscreen'} onClick={onFullscreenClicked} />
          )}
          {isFullScreen && (
            <Option
              label={'Exit Fullscreen'}
              onClick={onExitFullscreenClicked}
            />
          )}
          {!isTemplated && (
            <>
              {/* Where Edit opens Studio, content is added there */}
              {!canOpenStudio && (
                <PermissionGates.Has
                  requiredPermission={PERMISSIONS.DASHBOARDS.EDIT}
                >
                  <Option
                    label={'Add Visualization'}
                    subMenuPlacement={'left'}
                    subMenu={
                      <>
                        <Option
                          label={'Create new gadget'}
                          onClick={onCreateNewGadgetClicked}
                        />
                        <Option
                          label={'Add metric list'}
                          onClick={onMetricListAdded}
                        />
                        <Option
                          label={'Add KPI List'}
                          onClick={onAddScorecardClicked}
                          isDisabled={isTemplate}
                          disabledMessage="KPI Lists are not available on templates yet"
                        />
                        <Option
                          label={'Add image card'}
                          onClick={onImageCardAdded}
                        />
                        <Option
                          label={'Add video card'}
                          onClick={openVideoCardForm}
                        />
                        <Option
                          label={'Add text card'}
                          onClick={openTextCardForm}
                        />
                        <Option label={'Add IFrame'} onClick={openIFrameForm} />
                      </>
                    }
                  />
                </PermissionGates.Has>
              )}
              {!isWallboard && (
                <PermissionGates.Has
                  requiredPermission={PERMISSIONS.DASHBOARDS.CREATE}
                >
                  <Option label={'Save as'} onClick={onSaveAs} />
                </PermissionGates.Has>
              )}
            </>
          )}
          {isTemplate && (
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.DASHBOARDS.CREATE_TEMPLATE}
            >
              <Option
                label={'Variable Filters'}
                onClick={() => {
                  openVariableFiltersSlideOut();
                }}
              />
            </PermissionGates.Has>
          )}
          {!isWallboard && !isTemplate && !isTemplated && (
            <FeatureGate featureName={FEATURE_GATES.TEMPLATES}>
              <PermissionGates.Has
                requiredPermission={PERMISSIONS.DASHBOARDS.CREATE_TEMPLATE}
              >
                <Option
                  isDisabled={isScorecardDashboard}
                  disabledMessage={
                    'KPI Lists are not supported in templates yet'
                  }
                  label={'Create Template From'}
                  onClick={createTemplateFrom}
                />
              </PermissionGates.Has>
            </FeatureGate>
          )}
          {isTemplated && (
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.DASHBOARDS.CREATE_TEMPLATE}
            >
              <Option
                label={'Configure Variables'}
                onClick={onConfigureVariables}
              />

              <>
                {dashboard.templateId && (
                  <Option
                    label={'View Source Template'}
                    onClick={() => onViewSourceTemplate(dashboard.templateId)}
                  />
                )}
              </>

              <Option label={'Save as'} onClick={onSaveAs} />
            </PermissionGates.Has>
          )}
          <FleetOpsStaffOnly>
            <Option
              label={'Report an issue'}
              onClick={openReportAnIssue}
              testId="report-an-issue"
            />
          </FleetOpsStaffOnly>
          <Option label={'Show Usage'} onClick={onUsageReportClicked} />
          {!isWallboard && !isTemplate && (
            <FeatureGate featureName={FEATURE_GATES.SCHEDULED_REPORTS}>
              <PermissionGates.Has
                requiredPermission={PERMISSIONS.DASHBOARDS.CREATE}
              >
                <Option label={'Schedule'} onClick={onScheduleClicked} />
              </PermissionGates.Has>
            </FeatureGate>
          )}
          <Info {...dashboard} />
          <V3UnpublishDashboard dashboard={dashboard} />
          {!isWallboard && isDeletable && (
            <PermissionGates.Has
              requiredPermission={
                isCreatedByMe
                  ? PERMISSIONS.DASHBOARDS.DELETE_CREATED_BY_ME
                  : PERMISSIONS.DASHBOARDS.DELETE_ANY_DASHBOARD
              }
            >
              <>
                <Break />
                <Option label={'Delete'} onClick={onDelete} isDanger />
              </>
            </PermissionGates.Has>
          )}
          {!isWallboard && !isDeletable && (
            <>
              <Break />
              <Tooltip
                content={
                  "You cannot delete this template until you have first deleted all of it's instances"
                }
              >
                <Option
                  isDanger
                  label={'Delete'}
                  onClick={() => {
                    alert(
                      "You cannot delete this template until you have first deleted all of it's instances",
                    );
                  }}
                />
              </Tooltip>
            </>
          )}
        </>
      </ContextMenu>
      {reportAnIssueModal}
    </>
  );
};

export default DashboardMenu;
